import { create } from 'zustand';

/**
 * The web app as an installed program: a new version waiting to be taken, the game saved for offline use,
 * and the browser's offer to install it. The service worker is registered only in a production web build
 * (the native shells carry their files with them), and a new version is never taken by itself: the player
 * decides, so that a heist is not reloaded under their hands.
 */

/** The install offer a Chromium browser makes (`beforeinstallprompt`); not in the standard typings. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaState {
  /** A new version is installed in the background and waits for the player. */
  needRefresh: boolean;
  /** The files are saved: the game opens without a network. Told once. */
  offlineReady: boolean;
  /** The browser would install the app if asked. */
  canInstall: boolean;
  /** Takes the new version: the page reloads. */
  applyUpdate: () => void;
  /** Dismisses the notice of a new version until the next start. */
  later: () => void;
  acknowledgeOffline: () => void;
  /** Asks the browser to install the app; resolves to whether the player agreed. */
  install: () => Promise<boolean>;
}

let installEvent: InstallPromptEvent | null = null;
let doUpdate: (() => void) | null = null;

export const usePwaStore = create<PwaState>((set) => ({
  needRefresh: false,
  offlineReady: false,
  canInstall: false,
  applyUpdate: () => {
    doUpdate?.();
  },
  later: () => {
    set({ needRefresh: false });
  },
  acknowledgeOffline: () => {
    set({ offlineReady: false });
  },
  install: async () => {
    const event = installEvent;
    if (!event) return false;
    installEvent = null;
    set({ canInstall: false });
    await event.prompt();
    return (await event.userChoice).outcome === 'accepted';
  },
}));

/** The service worker checks for a new version this often while the app stays open. */
const UPDATE_CHECK_MS = 60 * 60_000;

/** True where the files come with the app (a native shell): there is nothing to install or to cache. */
function inShell(): boolean {
  return Boolean(import.meta.env.VITE_SHELL);
}

export async function startPwa(): Promise<void> {
  if (typeof window === 'undefined' || inShell()) return;
  window.addEventListener('beforeinstallprompt', (event) => {
    // The browser's own mini-bar would appear by itself; the app offers the choice in its settings instead.
    event.preventDefault();
    installEvent = event as InstallPromptEvent;
    usePwaStore.setState({ canInstall: true });
  });
  window.addEventListener('appinstalled', () => {
    installEvent = null;
    usePwaStore.setState({ canInstall: false });
  });
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  // The worker is registered here and not through the plugin's own module (`virtual:pwa-register`), which
  // the development server does not make: the same Workbox client, the same messages.
  const { Workbox } = await import('workbox-window');
  const worker = new Workbox('/sw.js');
  let asked = false;
  worker.addEventListener('installed', (event) => {
    // The first install has saved every file: from now on the game opens without a network.
    if (!event.isUpdate) usePwaStore.setState({ offlineReady: true });
  });
  worker.addEventListener('waiting', () => {
    usePwaStore.setState({ needRefresh: true });
  });
  worker.addEventListener('controlling', () => {
    // The new version took over because the player asked for it: load the page again with its files.
    if (asked) window.location.reload();
  });
  doUpdate = () => {
    asked = true;
    worker.messageSkipWaiting();
  };
  // A browser that will not keep a worker (a private window, a policy, a blocked page) makes the library
  // throw while it reads the registration it did not get: the game is then played without the install.
  const registration = await worker.register().catch(() => undefined);
  if (registration) setInterval(() => void registration.update(), UPDATE_CHECK_MS);
}
